/**
 * Home dashboard widgets, settings wiring, streak placement, layout hooks.
 */
import { apiPath } from "../lib/apiPath";
import { fetchModePlayCounts, fetchShopCatalog, type ShopCosmeticDto } from "../lib/cloud";
import { touchAccountSettingsUpdatedAt } from "./account-sync";
import { fetchChallengeStatus } from "./challenges/api";
import { getDotsBalance } from "./dots-currency";
import {
  enterHomeLayoutEdit,
  exitHomeLayoutEdit,
  isHomeLayoutEditing,
  mountHomeLayoutCustomize,
  resetHomeLayout,
  syncHomeWidgetLayouts,
} from "./home-layout";
import { mountHomeCursorBounce, setHomeCursorBounceEnabled, syncHomeCursorBounceVisibility } from "./home-cursor-bounce";
import {
  getBoolPref,
  getHomeFriendsFilter,
  homeBoolDefault,
  setBoolPref,
  setHomeFriendsFilter,
  type HomeBoolSettingKey,
  type HomeFriendsFilter,
} from "./home-prefs";
import { getAllModeIds, getLastPlayedMode, getModeTitle } from "./modes-screen";
import { ensureHomePlayStreakUi, ensureSideNavPlayStreakUi } from "./play-streaks";

export type HomeDashboardHooks = {
  isSignedIn: () => boolean;
  getAccountId: () => string | null;
  openChallenges: () => void;
  playMode: (modeId: string) => void;
  openLootDropLobby: () => void;
  pushSettingsUndo: () => void;
  applySettings: () => void;
  isTouchUi: () => boolean;
  hideSettingsAndShowHome: () => void;
  getCursorColor: () => string;
};

type FriendRow = {
  id: string;
  displayName: string;
  online?: boolean;
  activity?: string | null;
};

let hooks: HomeDashboardHooks | null = null;
let mounted = false;
let challengeTimer: ReturnType<typeof setInterval> | null = null;
let refreshTimer: ReturnType<typeof setInterval> | null = null;

const WIDGET_IDS = [
  "friends",
  "lastMode",
  "trending",
  "dots",
  "challenge",
  "lootdrops",
  "newInShop",
  "suggested",
  "continuePlaying",
] as const;

function startScreen(): HTMLElement | null {
  return document.getElementById("startScreen");
}

function isHomeVisible(): boolean {
  const el = startScreen();
  return !!el && !el.classList.contains("hidden");
}

function layer(): HTMLElement {
  let n = document.getElementById("homeWidgetsLayer");
  if (!n) {
    n = document.createElement("div");
    n.id = "homeWidgetsLayer";
    n.className = "homeWidgetsLayer";
    startScreen()?.appendChild(n);
  }
  return n;
}

function syncToggle(btn: HTMLElement | null, on: boolean) {
  if (!btn) return;
  btn.classList.toggle("toggleOn", on);
  btn.setAttribute("aria-pressed", on ? "true" : "false");
}

function readBool(key: HomeBoolSettingKey): boolean {
  return getBoolPref(key, homeBoolDefault(key));
}

function writeBool(key: HomeBoolSettingKey, on: boolean) {
  setBoolPref(key, on);
  touchAccountSettingsUpdatedAt();
}

function ensureWidget(id: string, title: string): HTMLElement {
  const root = layer();
  let el = root.querySelector(`[data-home-widget="${id}"]`) as HTMLElement | null;
  if (!el) {
    el = document.createElement("div");
    el.className = `homeWidget homeWidget--${id} hidden`;
    el.dataset.homeWidget = id;
    const h = document.createElement("h3");
    h.className = "homeWidgetTitle";
    h.textContent = title;
    const body = document.createElement("div");
    body.className = "homeWidgetBody";
    el.appendChild(h);
    el.appendChild(body);
    root.appendChild(el);
  }
  return el;
}

function widgetBody(el: HTMLElement): HTMLElement {
  let b = el.querySelector(".homeWidgetBody") as HTMLElement | null;
  if (!b) {
    b = document.createElement("div");
    b.className = "homeWidgetBody";
    el.appendChild(b);
  }
  return b;
}

function setWidgetVisible(el: HTMLElement, on: boolean) {
  el.classList.toggle("hidden", !on);
}

function formatCountdown(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(days)}:${pad(hours)}:${pad(mins)}:${pad(secs)}`;
}

function dailySuggestedModeId(): string {
  const ids = getAllModeIds();
  if (!ids.length) return "normal";
  const d = new Date();
  const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return ids[h % ids.length]!;
}

function syncStreakPlacement() {
  const showOnHome = readBool("settingHomeShowStreak");
  const home = document.getElementById("homePlayStreak");
  const side = document.getElementById("sideNavPlayStreak");
  // Always refresh both badges — play-day commits only call refreshAllPlayStreakUi,
  // and the visible streak may be the sidebar when Show Streak is off.
  ensureHomePlayStreakUi();
  ensureSideNavPlayStreakUi();
  if (home) home.classList.toggle("hidden", !showOnHome);
  if (side) side.classList.toggle("hidden", showOnHome);
}

async function refreshFriendsWidget() {
  const el = ensureWidget("friends", "Friends");
  const on = readBool("settingHomeShowFriends");
  setWidgetVisible(el, on);
  if (!on) return;
  const body = widgetBody(el);
  if (!hooks?.isSignedIn()) {
    body.textContent = "Sign in to see friends.";
    return;
  }
  try {
    const r = await fetch(apiPath("/api/friends"), { credentials: "include" });
    if (!r.ok) {
      body.textContent = "Could not load friends.";
      return;
    }
    const d = await r.json();
    const friends = (Array.isArray(d.friends) ? d.friends : []) as FriendRow[];
    const filter = getHomeFriendsFilter();
    const list = filter === "online" ? friends.filter((f) => !!f.online) : friends;
    if (!list.length) {
      body.textContent = filter === "online" ? "No friends online." : "No friends yet.";
      return;
    }
    const ul = document.createElement("ul");
    ul.className = "homeWidgetList";
    for (const f of list.slice(0, 12)) {
      const li = document.createElement("li");
      const dot = document.createElement("span");
      dot.className = `homeFriendDot${f.online ? " homeFriendDot--online" : ""}`;
      const name = document.createElement("span");
      name.textContent = f.displayName || "Friend";
      li.appendChild(dot);
      li.appendChild(name);
      ul.appendChild(li);
    }
    body.replaceChildren(ul);
  } catch {
    body.textContent = "Could not load friends.";
  }
}

function refreshLastModeWidget() {
  const el = ensureWidget("lastMode", "Last Mode");
  const on = readBool("settingHomeShowLastMode");
  setWidgetVisible(el, on);
  if (!on) return;
  const last = getLastPlayedMode();
  widgetBody(el).textContent = last ? last.title : "Play a mode to track this.";
}

async function refreshTrendingWidget() {
  const el = ensureWidget("trending", "Trending");
  const on = readBool("settingHomeShowTrending");
  setWidgetVisible(el, on);
  if (!on) return;
  const body = widgetBody(el);
  try {
    let counts: Record<string, number> = {};
    try {
      const w = await fetchModePlayCounts({ window: "24h" });
      counts = w.counts || {};
    } catch {
      const all = await fetchModePlayCounts();
      counts = all.counts || {};
    }
    let bestId = "";
    let bestN = 0;
    for (const [id, n] of Object.entries(counts)) {
      if (n > bestN) {
        bestN = n;
        bestId = id;
      }
    }
    body.textContent = bestId && bestN > 0 ? `${getModeTitle(bestId)} · ${bestN.toLocaleString("en-US")}` : "No data yet.";
  } catch {
    body.textContent = "Unavailable.";
  }
}

function refreshDotsWidget() {
  const el = ensureWidget("dots", "Dots");
  const on = readBool("settingHomeShowDots");
  setWidgetVisible(el, on);
  if (!on) return;
  const n = Math.max(0, Math.floor(getDotsBalance() || 0));
  widgetBody(el).textContent = `${n.toLocaleString("en-US")} Dots`;
}

function refreshChallengeWidget() {
  const el = ensureWidget("challenge", "Challenge");
  const on = readBool("settingHomeShowChallenge");
  setWidgetVisible(el, on);
  if (!on) {
    if (challengeTimer) {
      clearInterval(challengeTimer);
      challengeTimer = null;
    }
    return;
  }
  const body = widgetBody(el);
  void (async () => {
    if (!hooks?.isSignedIn()) {
      body.textContent = "Sign in for daily Challenges.";
      return;
    }
    try {
      const st = await fetchChallengeStatus();
      if (!st) {
        body.textContent = "Sign in for daily Challenges.";
        return;
      }
      const blocked =
        st.dayStatus === "success" || st.dayStatus === "failed" || st.dayStatus === "in_progress" || !st.available;
      const square = document.createElement("div");
      square.className = `homeChallengeSquare ${blocked ? "homeChallengeSquare--red" : "homeChallengeSquare--blue"}`;
      if (!blocked) {
        const play = document.createElement("button");
        play.type = "button";
        play.className = "btn green";
        play.textContent = "Play";
        play.addEventListener("click", (e) => {
          e.stopPropagation();
          hooks?.openChallenges();
        });
        square.appendChild(play);
      } else {
        const ends = st.dayEndsAt ? Date.parse(st.dayEndsAt) : NaN;
        const label = document.createElement("span");
        label.className = "homeChallengeCountdown";
        const tick = () => {
          if (!Number.isFinite(ends)) {
            label.textContent = "Locked";
            return;
          }
          label.textContent = formatCountdown(Math.max(0, ends - Date.now()));
        };
        tick();
        if (challengeTimer) clearInterval(challengeTimer);
        challengeTimer = setInterval(tick, 1000);
        square.appendChild(label);
      }
      body.replaceChildren(square);
    } catch {
      body.textContent = "Unavailable.";
    }
  })();
}

async function refreshLootDropsWidget() {
  const el = ensureWidget("lootdrops", "Active LootDrops");
  const on = readBool("settingHomeShowLootDrops");
  setWidgetVisible(el, on);
  if (!on) return;
  const body = widgetBody(el);
  if (!hooks?.isSignedIn()) {
    body.textContent = "Sign in to see friend rooms.";
    return;
  }
  try {
    const r = await fetch(apiPath("/api/friends"), { credentials: "include" });
    if (!r.ok) {
      body.textContent = "Unavailable.";
      return;
    }
    const d = await r.json();
    const friends = (Array.isArray(d.friends) ? d.friends : []) as FriendRow[];
    const active = friends.filter((f) => {
      const a = String(f.activity || "").toLowerCase();
      return !!f.online && (a.includes("loot") || a.includes("lootdrop"));
    });
    if (!active.length) {
      body.textContent = "No friend LootDrops right now.";
      const actions = document.createElement("div");
      actions.className = "homeWidgetActions";
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "btn";
      btn.textContent = "Open LootDrops";
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        hooks?.openLootDropLobby();
      });
      actions.appendChild(btn);
      body.appendChild(actions);
      return;
    }
    const ul = document.createElement("ul");
    ul.className = "homeWidgetList";
    for (const f of active.slice(0, 6)) {
      const li = document.createElement("li");
      li.textContent = f.displayName || "Friend";
      ul.appendChild(li);
    }
    const actions = document.createElement("div");
    actions.className = "homeWidgetActions";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "btn green";
    btn.textContent = "Join";
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      hooks?.openLootDropLobby();
    });
    actions.appendChild(btn);
    body.replaceChildren(ul, actions);
  } catch {
    body.textContent = "Unavailable.";
  }
}

/** Stable “has new shop items” flag so we never flash the widget while loading. */
let newInShopHasItems: boolean | null = null;
let newInShopFetchGen = 0;
let newInShopInFlight: Promise<void> | null = null;

function renderNewInShopItems(el: HTMLElement, recent: ShopCosmeticDto[]) {
  const body = widgetBody(el);
  const row = document.createElement("div");
  row.className = "homeShopCarousel";
  for (const it of recent.slice(0, 8)) {
    const card = document.createElement("div");
    card.className = "homeShopCarouselItem";
    if (
      it.imageData &&
      (it.imageData.startsWith("data:") || it.imageData.startsWith("/") || it.imageData.startsWith("http"))
    ) {
      const img = document.createElement("img");
      img.src = it.imageData;
      img.alt = "";
      card.appendChild(img);
    }
    const name = document.createElement("div");
    name.textContent = it.name || "Item";
    card.appendChild(name);
    row.appendChild(card);
  }
  body.replaceChildren(row);
}

async function refreshNewInShopWidget() {
  const el = ensureWidget("newInShop", "New in Shop");
  const on = readBool("settingHomeShowNewInShop");
  if (!on) {
    setWidgetVisible(el, false);
    return;
  }
  // Keep current visibility while refreshing — never show empty then hide (flicker).
  setWidgetVisible(el, newInShopHasItems === true);

  if (newInShopInFlight) {
    await newInShopInFlight;
    setWidgetVisible(el, readBool("settingHomeShowNewInShop") && newInShopHasItems === true);
    return;
  }

  const gen = ++newInShopFetchGen;
  newInShopInFlight = (async () => {
    try {
      const cat = await fetchShopCatalog();
      if (gen !== newInShopFetchGen) return;
      const items = (cat.items || []) as ShopCosmeticDto[];
      const cutoff = Date.now() - 48 * 60 * 60 * 1000;
      const recent = items.filter((it) => {
        const raw = (it as ShopCosmeticDto & { createdAt?: string | null }).createdAt;
        if (!raw) return false;
        const t = Date.parse(raw);
        return Number.isFinite(t) && t >= cutoff && !it.crateOnly && !it.sentOnly;
      });
      newInShopHasItems = recent.length > 0;
      if (!readBool("settingHomeShowNewInShop")) {
        setWidgetVisible(el, false);
        return;
      }
      if (!recent.length) {
        setWidgetVisible(el, false);
        return;
      }
      renderNewInShopItems(el, recent);
      setWidgetVisible(el, true);
    } catch {
      if (gen !== newInShopFetchGen) return;
      // On error keep last known good state (or stay hidden if never loaded).
      setWidgetVisible(el, readBool("settingHomeShowNewInShop") && newInShopHasItems === true);
    } finally {
      if (gen === newInShopFetchGen) newInShopInFlight = null;
    }
  })();
  await newInShopInFlight;
}

function refreshSuggestedWidget() {
  const el = ensureWidget("suggested", "Suggested");
  const on = readBool("settingHomeShowSuggested");
  setWidgetVisible(el, on);
  if (!on) return;
  const id = dailySuggestedModeId();
  const body = widgetBody(el);
  const title = document.createElement("div");
  title.textContent = getModeTitle(id);
  const actions = document.createElement("div");
  actions.className = "homeWidgetActions";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "btn green";
  btn.textContent = "Play";
  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    hooks?.playMode(id);
  });
  actions.appendChild(btn);
  body.replaceChildren(title, actions);
}

function refreshContinueWidget() {
  const el = ensureWidget("continuePlaying", "Continue Playing");
  const on = readBool("settingHomeShowContinue");
  const last = getLastPlayedMode();
  const show = on && !!last;
  setWidgetVisible(el, show);
  if (!show || !last) return;
  const body = widgetBody(el);
  const actions = document.createElement("div");
  actions.className = "homeWidgetActions";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "btn green";
  btn.textContent = `Continue: ${last.title}`;
  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    hooks?.playMode(last.id);
  });
  actions.appendChild(btn);
  body.replaceChildren(actions);
}

function movableIds(): string[] {
  const ids: string[] = ["start"];
  if (readBool("settingHomeShowFriends")) ids.push("friends");
  if (readBool("settingHomeShowLastMode")) ids.push("lastMode");
  if (readBool("settingHomeShowTrending")) ids.push("trending");
  if (readBool("settingHomeShowDots")) ids.push("dots");
  if (readBool("settingHomeShowStreak")) {
    /* streak under title is not a free widget by default; skip unless laid out */
  }
  if (readBool("settingHomeShowChallenge")) ids.push("challenge");
  if (readBool("settingHomeShowLootDrops")) ids.push("lootdrops");
  if (readBool("settingHomeShowNewInShop")) {
    const el = document.querySelector('[data-home-widget="newInShop"]');
    if (el && !el.classList.contains("hidden")) ids.push("newInShop");
  }
  if (readBool("settingHomeShowSuggested")) ids.push("suggested");
  if (readBool("settingHomeShowContinue") && getLastPlayedMode()) ids.push("continuePlaying");
  return ids;
}

function getWidgetEl(id: string): HTMLElement | null {
  if (id === "start") return document.getElementById("openModes");
  return document.querySelector(`[data-home-widget="${id}"]`);
}

function bounceObstacles(): DOMRect[] {
  const rects: DOMRect[] = [];
  const push = (el: Element | null) => {
    if (!el || !(el instanceof HTMLElement) || el.classList.contains("hidden")) return;
    if (el.id === "sideNav" || el.id === "sideNavToggle") return;
    const r = el.getBoundingClientRect();
    if (r.width > 2 && r.height > 2) rects.push(r);
  };
  push(document.querySelector("#startScreen .homeTitle"));
  push(document.getElementById("authStatusLine"));
  push(document.getElementById("openModes"));
  push(document.getElementById("homeSignInBtn"));
  for (const id of WIDGET_IDS) push(getWidgetEl(id));
  // Open news/notifications panel bodies count as obstacles; tabs too — sidebar excluded.
  const news = document.getElementById("homeNewsPanel");
  if (news && !news.classList.contains("hidden") && news.classList.contains("homeNewsPanel--open")) {
    push(news.querySelector(".homeNewsPanelBody"));
  }
  const notif = document.getElementById("homeNotificationsPanel");
  if (notif && !notif.classList.contains("hidden")) {
    push(notif.querySelector(".homeNotificationsPanelBody"));
  }
  return rects;
}

export function refreshHomeSettingsToggleUi() {
  const pairs: Array<[string, HomeBoolSettingKey]> = [
    ["toggleNewsInHomeBtn", "settingNewsInHome"],
    ["toggleNotificationsInHomeBtn", "settingNotificationsInHome"],
    ["toggleHomeSplashBtn", "settingHomeSplash"],
    ["toggleHomeShowFriendsBtn", "settingHomeShowFriends"],
    ["toggleHomeShowLastModeBtn", "settingHomeShowLastMode"],
    ["toggleHomeCursorBounceBtn", "settingHomeCursorBounce"],
    ["toggleHomeShowTrendingBtn", "settingHomeShowTrending"],
    ["toggleHomeShowDotsBtn", "settingHomeShowDots"],
    ["toggleHomeShowStreakBtn", "settingHomeShowStreak"],
    ["toggleHomeShowChallengeBtn", "settingHomeShowChallenge"],
    ["toggleHomeShowLootDropsBtn", "settingHomeShowLootDrops"],
    ["toggleHomeShowNewInShopBtn", "settingHomeShowNewInShop"],
    ["toggleHomeShowSuggestedBtn", "settingHomeShowSuggested"],
    ["toggleHomeShowContinueBtn", "settingHomeShowContinue"],
  ];
  for (const [id, key] of pairs) {
    syncToggle(document.getElementById(id), readBool(key));
  }
  const friendsOn = readBool("settingHomeShowFriends");
  document.getElementById("homeFriendsFilterRow")?.classList.toggle("hidden", !friendsOn);
  const filter = getHomeFriendsFilter();
  document.querySelectorAll<HTMLButtonElement>("[data-home-friends-filter]").forEach((btn) => {
    const v = btn.getAttribute("data-home-friends-filter") as HomeFriendsFilter;
    const on = v === filter;
    btn.classList.toggle("settingsSegBtn--active", on);
    btn.setAttribute("aria-pressed", on ? "true" : "false");
  });
}

function wireToggle(btnId: string, key: HomeBoolSettingKey) {
  const btn = document.getElementById(btnId);
  if (!btn || btn.dataset.homeWired === "1") return;
  btn.dataset.homeWired = "1";
  btn.addEventListener("click", () => {
    hooks?.pushSettingsUndo();
    writeBool(key, !readBool(key));
    hooks?.applySettings();
  });
}

export function refreshHomeDashboard() {
  if (!mounted) return;
  syncStreakPlacement();
  void refreshFriendsWidget();
  refreshLastModeWidget();
  void refreshTrendingWidget();
  refreshDotsWidget();
  refreshChallengeWidget();
  void refreshLootDropsWidget();
  void refreshNewInShopWidget();
  refreshSuggestedWidget();
  refreshContinueWidget();
  syncHomeWidgetLayouts(movableIds());
  setHomeCursorBounceEnabled(readBool("settingHomeCursorBounce"));
  syncHomeCursorBounceVisibility();
  refreshHomeSettingsToggleUi();
}

export function applyHomeDashboardFromSettings() {
  refreshHomeSettingsToggleUi();
  if (isHomeVisible()) refreshHomeDashboard();
  else {
    syncStreakPlacement();
    setHomeCursorBounceEnabled(readBool("settingHomeCursorBounce"));
    syncHomeCursorBounceVisibility();
  }
}

export function mountHomeDashboard(h: HomeDashboardHooks) {
  hooks = h;
  if (mounted) {
    refreshHomeDashboard();
    return;
  }
  mounted = true;
  layer();
  for (const id of WIDGET_IDS) {
    const titles: Record<string, string> = {
      friends: "Friends",
      lastMode: "Last Mode",
      trending: "Trending",
      dots: "Dots",
      challenge: "Challenge",
      lootdrops: "Active LootDrops",
      newInShop: "New in Shop",
      suggested: "Suggested",
      continuePlaying: "Continue Playing",
    };
    ensureWidget(id, titles[id] || id);
  }

  wireToggle("toggleHomeShowFriendsBtn", "settingHomeShowFriends");
  wireToggle("toggleHomeShowLastModeBtn", "settingHomeShowLastMode");
  wireToggle("toggleHomeCursorBounceBtn", "settingHomeCursorBounce");
  wireToggle("toggleHomeShowTrendingBtn", "settingHomeShowTrending");
  wireToggle("toggleHomeShowDotsBtn", "settingHomeShowDots");
  wireToggle("toggleHomeShowStreakBtn", "settingHomeShowStreak");
  wireToggle("toggleHomeShowChallengeBtn", "settingHomeShowChallenge");
  wireToggle("toggleHomeShowLootDropsBtn", "settingHomeShowLootDrops");
  wireToggle("toggleHomeShowNewInShopBtn", "settingHomeShowNewInShop");
  wireToggle("toggleHomeShowSuggestedBtn", "settingHomeShowSuggested");
  wireToggle("toggleHomeShowContinueBtn", "settingHomeShowContinue");

  document.querySelectorAll<HTMLButtonElement>("[data-home-friends-filter]").forEach((btn) => {
    if (btn.dataset.homeWired === "1") return;
    btn.dataset.homeWired = "1";
    btn.addEventListener("click", () => {
      const v = btn.getAttribute("data-home-friends-filter");
      if (v !== "online" && v !== "all") return;
      if (v === getHomeFriendsFilter()) return;
      hooks?.pushSettingsUndo();
      setHomeFriendsFilter(v);
      touchAccountSettingsUpdatedAt();
      hooks?.applySettings();
    });
  });

  const customizeBtn = document.getElementById("homeCustomizeLayoutBtn");
  if (customizeBtn && customizeBtn.dataset.homeWired !== "1") {
    customizeBtn.dataset.homeWired = "1";
    customizeBtn.addEventListener("click", () => {
      hooks?.hideSettingsAndShowHome();
      enterHomeLayoutEdit();
    });
  }
  const resetBtn = document.getElementById("homeResetLayoutBtn");
  if (resetBtn && resetBtn.dataset.homeWired !== "1") {
    resetBtn.dataset.homeWired = "1";
    resetBtn.addEventListener("click", () => {
      hooks?.pushSettingsUndo();
      resetHomeLayout();
      touchAccountSettingsUpdatedAt();
      refreshHomeDashboard();
      hooks?.applySettings();
    });
  }

  mountHomeLayoutCustomize({
    getRoot: startScreen,
    getWidgetEl,
    getMovableIds: movableIds,
    onChanged: () => touchAccountSettingsUpdatedAt(),
    isTouchUi: () => !!hooks?.isTouchUi(),
  });

  mountHomeCursorBounce({
    getObstacles: bounceObstacles,
    getColor: () => hooks?.getCursorColor() || "#ffffff",
    isActive: () => isHomeVisible() && readBool("settingHomeCursorBounce") && !isHomeLayoutEditing(),
  });

  const screen = startScreen();
  if (screen) {
    const obs = new MutationObserver(() => {
      if (isHomeVisible()) refreshHomeDashboard();
      else {
        syncHomeCursorBounceVisibility();
        if (isHomeLayoutEditing()) exitHomeLayoutEdit();
      }
    });
    obs.observe(screen, { attributes: true, attributeFilter: ["class"] });
  }

  if (!refreshTimer) {
    refreshTimer = setInterval(() => {
      if (isHomeVisible()) {
        refreshDotsWidget();
        syncStreakPlacement();
      }
    }, 15000);
  }

  refreshHomeDashboard();
}

export { defaultHomeSettingValue, HOME_ALL_SETTING_KEYS, HOME_BOOL_SETTING_KEYS } from "./home-prefs";
